import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMeals, createMeal, updateMeal, deleteMeal, batchDeleteMeals,
} from '../../../services/mealService';

const useMealApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = params;
    setFetching(true);
    getMeals(params)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await createMeal(formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await updateMeal(id, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) await deleteMeal(ids[0]);
      else await batchDeleteMeals(ids);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('canteen.mealName'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'startTime', label: t('canteen.startTime'), options: { filter: false, sort: true, customBodyRender: (v) => v ? String(v).substring(0, 5) : '-' } },
    { name: 'endTime', label: t('canteen.endTime'), options: { filter: false, sort: false, customBodyRender: (v) => v ? String(v).substring(0, 5) : '-' } },
    { name: 'cost', label: t('canteen.cost'), options: { filter: false, sort: false, customBodyRender: (v, meta) => v != null ? `${v} ${meta.rowData?.[5] || ''}`.trim() : '-' } },
    { name: 'currency', label: t('canteen.currency'), options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'displayOrder', label: t('canteen.displayOrder'), options: { filter: false, sort: true } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('canteen.mealName'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('canteen.description'), type: 'textarea', grid: 12 },
    { name: 'startTime', label: t('canteen.startTime'), type: 'time', grid: 6, required: true },
    { name: 'endTime', label: t('canteen.endTime'), type: 'time', grid: 6, required: true },
    { name: 'cost', label: t('canteen.cost'), type: 'number', grid: 6 },
    {
      name: 'currency', label: t('canteen.currency'), type: 'select', grid: 6,
      options: [
        { id: 'TRY', name: 'TRY' },
        { id: 'USD', name: 'USD' },
        { id: 'EUR', name: 'EUR' },
      ],
      idKey: 'id', labelKey: 'name',
    },
    { name: 'displayOrder', label: t('canteen.displayOrder'), type: 'number', grid: 12 },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useMealApi;
